import { useContext } from "react";

import { Button, TextInput } from "flowbite-react";
import { useForm } from "react-hook-form";

import { Actions } from "../reducers/Booking";
import Stepper, { STEPS } from "../components/Stepper";
import { FaCar, FaMapMarkerAlt, FaTools, FaCalendarAlt } from "react-icons/fa";

import { MONTHS, APPOINTMENT_TYPE } from "../constants";
import AppContext from "../context/AppContext";

const Page = ({ state, dispatch, pushHash }) => {
  const confirm = (payload) => {
    dispatch({
      type: Actions.SetCustomer,
      payload,
    });
  };

  const {
    brand: {
      buttonPrimaryBorderColor,
      buttonPrimaryBackgroundColor,
      buttonPrimaryTextColor,
      inputBorderColor,
      inputBackgroundColor,
      inputTextColor,
      inputErrorBorderColor,
      inputErrorBackgroundColor,
      inputErrorTextColor,
    },
    fields,
  } = useContext(AppContext);

  const { customer } = fields || {};
  const requiredCustomerFields = customer || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      forename: "",
      surname: "",
      postcode: "",

      address1: "",
      address2: "",

      address3: "", // Town/City
      address4: "", // Country

      email: "",

      preferredPhone: 1,

      salutation: "",
      address5: "",
      address6: "",
      phone1: "", // Land line
      phone2: "", // Home phone
      phone3: "", // Mobile
      phone4: "",
      courtesyVehicleDriverDOB: "1970-01-01T00:00:00",
    },
  });

  return (
    <>
      <Stepper step={STEPS.Customer} pushHash={pushHash} />

      <h2 className="text-xl block mb-8">
        Please enter your contact information
      </h2>

      <form onSubmit={handleSubmit(confirm)}>
        <div className="grid grid-cols-2 gap-x-16 gap-y-4 mt-4">
          <div>
            <label>Title</label>
            <TextInput
              {...register("title", { required: true })}
              className="w-full"
              color={errors?.title ? "failure" : ""}
              style={
                errors?.title
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.title && (
              <p className="text-sm text-red-500">Title is required</p>
            )}
          </div>
          <div className="col-start-1">
            <label>First Name</label>
            <TextInput
              {...register("forename", { required: true })}
              className="w-full"
              color={errors?.forename ? "failure" : ""}
              style={
                errors?.forename
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.forename && (
              <p className="text-sm text-red-500">First Name is required</p>
            )}
          </div>
          <div>
            <label>Last Name</label>
            <TextInput
              {...register("surname", { required: true })}
              className="w-full"
              color={errors?.surname ? "failure" : ""}
              style={
                errors?.surname
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.surname && (
              <p className="text-sm text-red-500">Last Name is required</p>
            )}
          </div>
          <div>
            <label>Postcode</label>
            <TextInput
              {...register("postcode", { required: true })}
              className="w-full"
              color={errors?.postcode ? "failure" : ""}
              style={
                errors?.postcode
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.postcode && (
              <p className="text-sm text-red-500">Postcode is required</p>
            )}
          </div>
          <div className="col-start-1">
            <label>Address 1</label>
            <TextInput
              {...register("address1", { required: true })}
              className="w-full"
              color={errors?.address1 ? "failure" : ""}
              style={
                errors?.address1
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.address1 && (
              <p className="text-sm text-red-500">Address 1 is required</p>
            )}
          </div>
          <div>
            <label>Address 2</label>
            <TextInput
              {...register("address2", {
                required: requiredCustomerFields.includes("address2"),
              })}
              className="w-full"
              color={errors?.address2 ? "failure" : ""}
              style={
                errors?.address2
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.address2 && (
              <p className="text-sm text-red-500">Address 2 is required</p>
            )}
          </div>
          <div>
            <label>Town / City</label>
            <TextInput
              {...register("address3", {
                required: requiredCustomerFields.includes("address3"),
              })}
              className="w-full"
              color={errors?.address3 ? "failure" : ""}
              style={
                errors?.address3
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.address3 && (
              <p className="text-sm text-red-500">Town / City is required</p>
            )}
          </div>
          <div>
            <label>Country</label>
            <TextInput
              {...register("address4", {
                required: requiredCustomerFields.includes("address4"),
              })}
              className="w-full"
              color={errors?.address4 ? "failure" : ""}
              style={
                errors?.address4
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.address4 && (
              <p className="text-sm text-red-500">Country is required</p>
            )}
          </div>
          <div>
            <label>Landline</label>
            <TextInput
              {...register("phone1", {
                required: requiredCustomerFields.includes("phone1"),
              })}
              className="w-full"
              color={errors?.phone1 ? "failure" : ""}
              style={
                errors?.phone1
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.phone1 && (
              <p className="text-sm text-red-500">Landline is required</p>
            )}
          </div>
          <div>
            <label>Mobile</label>
            <TextInput
              {...register("phone3", {
                required: requiredCustomerFields.includes("phone3"),
              })}
              className="w-full"
              color={errors?.phone3 ? "failure" : ""}
              style={
                errors?.phone3
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.phone3 && (
              <p className="text-sm text-red-500">Mobile is required</p>
            )}
          </div>
          <div className="col-span-2">
            <label>Email address</label>
            <TextInput
              {...register("email", {
                required: requiredCustomerFields.includes("email"),
              })}
              className="w-full"
              color={errors?.email ? "failure" : ""}
              style={
                errors?.email
                  ? {
                      borderColor: inputErrorBorderColor,
                      backgroundColor: inputErrorBackgroundColor,
                      color: inputErrorTextColor,
                    }
                  : {
                      borderColor: inputBorderColor,
                      backgroundColor: inputBackgroundColor,
                      color: inputTextColor,
                    }
              }
            />
            {errors?.email && (
              <p className="text-sm text-red-500">Email is required</p>
            )}
          </div>
        </div>

        <div className="mt-8">
          <Button
            type="submit"
            color="dark"
            className="w-full md:w-3/4 md:m-auto"
            style={{
              borderColor: buttonPrimaryBorderColor,
              backgroundColor: buttonPrimaryBackgroundColor,
              color: buttonPrimaryTextColor,
            }}
          >
            Continue
          </Button>
        </div>
      </form>
    </>
  );
};

const Description = ({ state }) => {
  const {
    brand: { textColor, textShadeColor },
  } = useContext(AppContext);

  return (
    <>
      <h2 className="text-4xl font-bold" style={{ color: textColor }}>
        Your booking
      </h2>

      <div className="pb-4 pt-8" style={{ color: textColor }}>
        <FaCar size="2em" />
      </div>
      <h3 className="text-2xl font-bold pb-4" style={{ color: textColor }}>
        Your Vehicle
      </h3>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {state.vehicle.regnum}
      </p>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {state.vehicle.description}
      </p>

      <div className="pb-4 pt-8" style={{ color: textColor }}>
        <FaMapMarkerAlt size="2em" />
      </div>
      <h3 className="text-2xl font-bold pb-4" style={{ color: textColor }}>
        Selected Location
      </h3>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {state.dealer.description}
      </p>

      <div className="pb-4 pt-8" style={{ color: textColor }}>
        <FaTools size="2em" />
      </div>
      <h3 className="text-2xl font-bold pb-4" style={{ color: textColor }}>
        Your Service
      </h3>
      {state.repairOperations.map((ro, idx) => (
        <p
          className="text-xl text-slate-500"
          style={{ color: textShadeColor }}
          key={ro.code}
        >
          {ro.description}
        </p>
      ))}

      <div className="pb-4 pt-8" style={{ color: textColor }}>
        <FaCalendarAlt size="2em" />
      </div>
      <h3 className="text-2xl font-bold pb-4" style={{ color: textColor }}>
        Your Appointment
      </h3>
      <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
        {APPOINTMENT_TYPE[state.availability.value]}
      </p>
      {state.availability.date && state.availability.date.getMonth && (
        <p className="text-xl text-slate-500" style={{ color: textShadeColor }}>
          {MONTHS[state.availability.date.getMonth()]}{" "}
          {state.availability.date.getDate()},{" "}
          {state.availability.date.getFullYear()} at {state.availability.time}
        </p>
      )}
    </>
  );
};

const tuple = { Page, Description };

export default tuple;
